import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Calendar, ArrowLeft } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { usePageMeta } from "@/hooks/usePageMeta";
import { SITE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventDetailPage() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [notFound, setNotFound] = useState(false);
  usePageMeta(event?.title || "Event", event?.excerpt);

  useEffect(() => {
    axios.get(`${API}/events/${slug}`).then((r) => setEvent(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <>
        <Header />
        <main className="min-h-screen flex items-center justify-center bg-black pt-24">
          <div className="text-center">
            <h1 className="font-display text-5xl text-white">Event not found</h1>
            <Link to="/events" className="mt-6 inline-block px-6 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider">Back to events</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!event) {
    return <><Header /><main className="min-h-screen bg-black" /><Footer /></>;
  }

  return (
    <>
      <Header />
      <main>
        <section className="relative min-h-[60vh] flex items-end pt-24 pb-12 bg-black overflow-hidden">
          <div className="absolute inset-0">
            <img src={event.image || SITE.battle} alt="" className="w-full h-full object-cover opacity-45" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(90,24,154,0.45),transparent_60%)]" />
          </div>
          <div className="relative max-w-4xl mx-auto px-6 sm:px-8 w-full">
            <Link to="/events" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-brand-yellow mb-4"><ArrowLeft size={14} /> All events</Link>
            <h1 className="font-display text-5xl sm:text-7xl text-white tracking-wide leading-[0.95]">{event.title}</h1>
            <p className="mt-4 text-lg text-zinc-300 max-w-2xl">{event.excerpt}</p>
            <div className="mt-5 flex flex-wrap gap-4 text-xs uppercase tracking-[0.2em] text-zinc-400">
              {event.venue && <span className="inline-flex items-center gap-1.5"><MapPin size={13} className="text-brand-yellow" /> {event.venue}</span>}
              {event.starts_at && <span className="inline-flex items-center gap-1.5"><Calendar size={13} className="text-brand-yellow" /> {new Date(event.starts_at).toLocaleDateString()}</span>}
            </div>
            <div className="mt-7 flex gap-3 flex-wrap">
              <a href={event.cta_url || "#"} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover transition-all glow-yellow">
                {event.cta_label || "Register"}
              </a>
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white font-bold uppercase text-sm tracking-wider hover:bg-white hover:text-black transition-all">Ask a question</Link>
            </div>
          </div>
        </section>
        <article className="py-16 sm:py-20 bg-black">
          <div className="max-w-3xl mx-auto px-6 sm:px-8 prose prose-invert prose-zinc">
            <p className="text-lg text-zinc-200 leading-relaxed whitespace-pre-line">{event.body}</p>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
